// src/components/molecules/auth/auth-text-field.tsx
"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

type AuthTextFieldProps = Omit<React.ComponentProps<"input">, "id"> & {
  id: string;
  label: string;
  icon?: React.ReactNode;
  error?: string | null;
  hint?: string;
  trailing?: React.ReactNode;
  containerClassName?: string;
};

/**
 * Floating-label text field with a leading icon, used across the auth flow.
 * Works with both controlled state (login) and react-hook-form `register`
 * (spread via `...inputProps`, ref forwarded through React 19 ref-as-prop).
 */
export function AuthTextField({
  id,
  label,
  icon,
  error,
  hint,
  trailing,
  className,
  containerClassName,
  ...inputProps
}: AuthTextFieldProps) {
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        data-invalid={error ? "true" : undefined}
        className={cn(
          "group relative flex min-h-14 items-center gap-3 rounded-xl border border-brand-border bg-white px-4 transition-colors",
          "focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/30",
          "data-[invalid=true]:border-destructive data-[invalid=true]:ring-2 data-[invalid=true]:ring-destructive/20",
          containerClassName,
        )}
      >
        {icon ? (
          <span aria-hidden className="shrink-0 text-brand-ink">
            {icon}
          </span>
        ) : null}

        <div className="relative flex-1">
          <input
            {...inputProps}
            id={id}
            placeholder=" "
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              "peer w-full bg-transparent pb-1 pt-5 text-base text-brand-ink outline-none placeholder:text-transparent",
              "disabled:cursor-not-allowed disabled:opacity-60",
              className,
            )}
          />
          <label
            htmlFor={id}
            className={cn(
              "pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 text-sm font-semibold text-brand-ink/60 transition-all duration-150 motion-reduce:transition-none",
              "peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-brand",
              "peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-xs",
            )}
          >
            {label}
          </label>
        </div>

        {trailing}
      </div>

      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-sm text-brand-ink/60">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
